import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";

import BadgeImage from "../ui-elements/badge-image";
import ImageGrid from "../image-grid";
import TypographyContent from "../ui-elements/typography-content";
import { PostContentDetails } from "../../interface/posts";
import {
  formatDateAndTimeFromString,
  formatTextType,
} from "../../utils/date-setup";
import { useNavigate } from "react-router-dom";
import { useNotificationService } from "../../hooks/notification";
import { apiLikePost } from "../../api/post";
import { PATH_DASHBOARD } from "../../routes/path";
import TruncatedLimitContent from "../ui-elements/typography-limit-content";

export const AccountReportContent = ({
  post_details,
}: {
  post_details: PostContentDetails;
}) => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationService();
  const isMedium = useResponsive("down", "md", "xl");
  const [social_links, setSocial_links] = useState({
    likes: post_details?.likes,
    messages: post_details?.comments,
  });
  const [liked, setLiked] = useState(false);

  // Construct full URL
  const baseUrl = window.location.origin;
  const additionalPath = `/posts?post_id=${post_details.id}`;
  const fullShareLink = `${baseUrl}${additionalPath}`;
  // Copy function using Clipboard API
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullShareLink);
      showNotification("Link copied!", "success");
    } catch (err) {
      console.error("Failed to copy: ", err);
      showNotification("Failed to copy the link. Try again.", "error");
    }
  };

  const like_post = async (post_id: number) => {
    try {
      const response = await apiLikePost(post_id);
      if (response.status === 200) {
        if (response.data.message === "added") {
        } else {
        }
        //
      } else {
      }
    } catch (error) {
      //
    }
  };

  const handleLike = () => {
    setLiked(!liked);
  };

  const handle_like = (id: number) => {
    if (liked) {
      // remove one from current list
      setSocial_links((prev) => ({ ...prev, likes: prev?.likes - 1 }));

      handleLike();
      // send request
      like_post(id);
    } else {
      //  add one to current list
      setSocial_links((prev) => ({ ...prev, likes: prev?.likes + 1 }));
      handleLike();
      // send request
      like_post(id);
    }
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(231, 235, 239, 1)",
        my: 2,
        borderRadius: "8px",
        padding: 2,
        minHeight: 250,
        width: "94%",
        marginHorizontal: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          mb: 3,
        }}
      >
        <img
          src={post_details?.author_photo_url}
          style={{ width: 50, height: 50, borderRadius: 40 }}
        />
        <Stack>
          <Stack direction={"row"} spacing={1}>
            <Typography variant="subtitle2" color="black">
              {post_details?.author}
            </Typography>
            <BadgeImage badge={post_details?.author_badge} />
          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: "#4A5E6D",
              fontWeight: 400,
            }}
          >
            {formatDateAndTimeFromString(post_details?.created_at)}
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            sx={{
              fontWeight: 400,
              fontStyle: "italic",
            }}
          >
            {formatTextType(post_details?.post_type)}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ ml: 4 }}>
        <Stack>
          <Typography
            color="black"
            sx={{
              fontSize: 18,
              marginBottom: 2,
              fontWeight: 500,
            }}
          >
            {post_details?.title}
          </Typography>
          <ImageGrid
            images={post_details?.media} // Pass images as an array
          />
        </Stack>
        <Box sx={{ mt: "10px", display: "block" }}>
          <TruncatedLimitContent content={post_details?.context} />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            my: 2,
          }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            sx={{ cursor: "pointer" }}
            onClick={() =>
              navigate(PATH_DASHBOARD.post, {
                state: { postId: post_details?.id },
              })
            }
          >
            {post_details?.comments >= 1 ? (
              <img
                src={"assets/icons/social/message-text.svg"}
                style={{ width: 18, height: 18 }}
              />
            ) : (
              <img
                src={"assets/icons/social/message-text.png"}
                style={{ width: 18, height: 18 }}
              />
            )}

            <Typography
              variant="caption"
              sx={{
                color:
                  post_details?.comments >= 1
                    ? "#459173"
                    : "rgba(74, 94, 109, 1)",
              }}
            >
              {post_details?.comments} {isMedium ? "" : "comments"}
            </Typography>
          </Stack>

          <Stack
            direction={"row"}
            alignItems={"center"}
            sx={{ cursor: "pointer" }}
            spacing={1}
            onClick={() => handle_like(post_details?.id)}
          >
            {liked && social_links?.likes > 0 ? (
              <img
                src={"assets/icons/social/heart.svg"}
                style={{ width: 20, height: 19 }}
              />
            ) : (
              <img
                src={"assets/icons/social/like.png"}
                style={{ width: 18, height: 18 }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                color:
                  liked && social_links?.likes > 0
                    ? "#459173"
                    : "rgba(74, 94, 109, 1)",
              }}
            >
              {social_links?.likes}
            </Typography>
          </Stack>

          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            sx={{ cursor: "pointer" }}
            onClick={copyToClipboard}
          >
            <img
              src={"assets/icons/social/share.png"}
              style={{ width: 18, height: 18 }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "rgba(74, 94, 109, 1)",
              }}
            >
              {isMedium ? "" : "Share"}
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
